// Sante du parc — ce que la telemetrie des 7 applications dit, lisible sans le portail Azure.
//
// Maquette FIGEE : docs/standards/mockups/sante-du-parc-admin.html
// (DECISION-2026-09-24-MAQUETTE-SANTE-DU-PARC). Cadrage :
// docs/prompts/MASTER-LA-SANTE-DU-PARC-DANS-ADMIN-2026-09-24.md.
//
// LA REGLE D AFFICHAGE : ce qui n a pas pu etre lu s affiche « on ne sait pas », jamais 0
// et jamais en vert. Le serveur le porte dans chaque bloc (`etat`) ; la page ne fabrique
// aucun chiffre qu il n a pas rendu.
//
// @gs/ui tel quel (PageHeader, KpiCard, StatusBadge, GsDrawer, GsToggle, QueryError).
// KpiCard n a pas de ligne d aide : elle est posee SOUS la carte, sans toucher au composant.
import { useMemo, useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import {
  Activity,
  AlertTriangle,
  Bell,
  Bot,
  Building2,
  Check,
  ChevronRight,
  Clock,
  ExternalLink,
  EyeOff,
  Eye,
  Filter,
  Gauge,
  HelpCircle,
  Home,
  QrCode,
  RefreshCw,
  RotateCcw,
  Shield,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { GsDrawer, GsToggle, KpiCard, PageHeader, QueryError, StatusBadge } from '@gs/ui';
import type { StatusBadgeProps } from '@gs/ui';
import { useTranslation } from '../i18n';
import {
  GENRES,
  useParcSante,
  useRemesurerParcSante,
  type Alarme,
  type AppSante,
  type Couverture,
  type Genre,
  type Heure,
  type Motif,
  type SanteDuParc,
  type Statut,
} from '../hooks/useParcSante';

// ── Libelles : cles LITTERALES (une cle construite echappe a i18next-parser, qui la purge) ──

function nomDeLApp(id: string, t: TFunction): { nom: string; role: string; icone: LucideIcon } {
  switch (id) {
    case 'gs-hub': return { nom: t('admin:parc.app.hub.name'), role: t('admin:parc.app.hub.role'), icone: Home };
    case 'gs-ug': return { nom: t('admin:parc.app.ug.name'), role: t('admin:parc.app.ug.role'), icone: Building2 };
    case 'gs-gest': return { nom: t('admin:parc.app.gest.name'), role: t('admin:parc.app.gest.role'), icone: Users };
    case 'gs-admin': return { nom: t('admin:parc.app.admin.name'), role: t('admin:parc.app.admin.role'), icone: Shield };
    case 'gs-mcp': return { nom: t('admin:parc.app.mcp.name'), role: t('admin:parc.app.mcp.role'), icone: Bot };
    case 'gs-api-portail': return { nom: t('admin:parc.app.portail.name'), role: t('admin:parc.app.portail.role'), icone: QrCode };
    default: return { nom: id, role: '', icone: Activity };
  }
}

function libelleStatut(s: Statut, t: TFunction): string {
  switch (s) {
    case 'ok': return t('admin:parc.statut.ok');
    case 'erreurs': return t('admin:parc.statut.erreurs');
    case 'lente': return t('admin:parc.statut.lente');
    case 'illisible': return t('admin:parc.statut.illisible');
    case 'ne-repond-pas': return t('admin:parc.statut.neRepondPas');
  }
}

const VARIANTE_STATUT: Record<Statut, StatusBadgeProps['variant']> = {
  ok: 'success',
  erreurs: 'danger',
  lente: 'warning',
  illisible: 'warning',
  'ne-repond-pas': 'danger',
};

function libelleMotif(m: Motif, t: TFunction): string {
  switch (m.code) {
    case 'acces-refuse': return t('admin:parc.motif.accesRefuse', { http: m.http ?? 403 });
    case 'delai': return t('admin:parc.motif.delai');
    case 'configuration': return t('admin:parc.motif.configuration');
    case 'identite': return t('admin:parc.motif.identite');
    case 'reponse-invalide': return t('admin:parc.motif.reponseInvalide');
    case 'erreur-azure':
      return m.http ? t('admin:parc.motif.erreurAzure', { http: m.http }) : t('admin:parc.motif.erreurAzureSansCode');
  }
}

function libelleGenre(g: Genre, t: TFunction): { nom: string; description: string } {
  switch (g) {
    case 'silence': return { nom: t('admin:parc.genre.silence'), description: t('admin:parc.genre.silenceD') };
    case 'erreurs': return { nom: t('admin:parc.genre.erreurs'), description: t('admin:parc.genre.erreursD') };
    case 'disponibilite': return { nom: t('admin:parc.genre.disponibilite'), description: t('admin:parc.genre.disponibiliteD') };
    case 'redemarrages': return { nom: t('admin:parc.genre.redemarrages'), description: t('admin:parc.genre.redemarragesD') };
    case 'base': return { nom: t('admin:parc.genre.base'), description: t('admin:parc.genre.baseD') };
    case 'cloisonnement': return { nom: t('admin:parc.genre.cloisonnement'), description: t('admin:parc.genre.cloisonnementD') };
  }
}

function pourquoi(c: Couverture, genre: Genre, app: string, t: TFunction): string {
  const v = { k: libelleGenre(genre, t).nom, a: app };
  switch (c.etat) {
    case 'voit': return t('admin:parc.pourquoi.voit', v);
    case 'sans-objet': return t('admin:parc.pourquoi.sansObjet', v);
    case 'inconnue': return t('admin:parc.pourquoi.inconnue', v);
    case 'rarement':
      return c.raison === 'volume'
        ? t('admin:parc.pourquoi.rarementVolume', { ...v, n: c.n, total: c.total })
        : t('admin:parc.pourquoi.rarementVolumeSql', { ...v, n: c.n, total: c.total });
    case 'aveugle': {
      const correction = ` ${t('admin:parc.pourquoi.correction')}`;
      switch (c.raison) {
        case 'volume': return t('admin:parc.pourquoi.aveugleVolume', v);
        case 'volume-sql': return t('admin:parc.pourquoi.aveugleVolumeSql', v);
        case 'sans-requetes': return t('admin:parc.pourquoi.aveugleSansRequetes', v) + correction;
        case 'sans-sql': return t('admin:parc.pourquoi.aveugleSansSql', v) + correction;
        case 'sans-demarrage': return t('admin:parc.pourquoi.aveugleSansDemarrage', v) + correction;
        case 'sans-signal': return t('admin:parc.pourquoi.aveugleSansSignal', v);
      }
    }
  }
}

// ── Mise en forme ───────────────────────────────────────────────────────────────────────

function useFormats() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-CA' : 'fr-CA';
  return useMemo(() => {
    const heure = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' });
    const jourHeure = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
    const nombre = (n: number) => n.toLocaleString(locale);
    return {
      t,
      nombre,
      /** Un zero se lit « — » dans une case (DESIGN_SYSTEM : jamais « 0 » dans une cellule). */
      compte: (n: number) => (n === 0 ? '—' : nombre(n)),
      heure: (iso: string) => heure.format(new Date(iso)),
      jourHeure: (iso: string) => jourHeure.format(new Date(iso)),
      duree: (s: number) => {
        const min = Math.floor(s / 60);
        if (min < 60) return `${min} min`;
        return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')}`;
      },
      vitesse: (ms: number) =>
        ms < 1000 ? `${nombre(Math.round(ms))} ms` : `${(ms / 1000).toLocaleString(locale, { maximumFractionDigits: 1 })} s`,
    };
  }, [locale, t]);
}
type Formats = ReturnType<typeof useFormats>;

/** Bruit « identite indisponible » a l affichage : les entrees d EXCEPTIONS seulement. */
function bruitAffiche(app: AppSante): number {
  const t = app.telemetrie;
  if (t.etat === 'illisible') return 0;
  return t.bruit.entrees.filter((e) => e.source === 'exceptions').reduce((s, e) => s + e.n, 0);
}

function alarmesComptees(app: AppSante): Alarme[] {
  return app.alarmes.etat === 'ok' ? app.alarmes.liste.filter((a) => a.couverture.etat !== 'sans-objet') : [];
}

function totauxCouverture(apps: AppSante[]) {
  const tot = { voit: 0, rarement: 0, aveugle: 0, inconnue: 0, total: 0 };
  for (const app of apps) {
    for (const a of alarmesComptees(app)) {
      tot.total += 1;
      if (a.couverture.etat === 'voit') tot.voit += 1;
      else if (a.couverture.etat === 'rarement') tot.rarement += 1;
      else if (a.couverture.etat === 'aveugle') tot.aveugle += 1;
      else tot.inconnue += 1;
    }
  }
  return tot;
}

function declencheesDepuis24h(a: Alarme, mesureA: string): string[] {
  const depuis = Date.parse(mesureA) - 24 * 3_600_000;
  return a.declenchements.filter((d) => Date.parse(d) >= depuis);
}

/** 24 cases horaires se terminant a l heure de la mesure ; une heure sans demande reste vide. */
function casesHoraires(heures: Heure[], mesureA: string): (Heure | null)[] {
  const fin = new Date(mesureA);
  fin.setUTCMinutes(0, 0, 0);
  const parHeure = new Map(heures.map((h) => [Date.parse(h.h), h]));
  return Array.from({ length: 24 }, (_, i) => parHeure.get(fin.getTime() - (23 - i) * 3_600_000) ?? null);
}

// ── Petits blocs ────────────────────────────────────────────────────────────────────────

function Carte({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800 ${className}`}>
      {children}
    </div>
  );
}

function Barres({ cases, hauteur, fine }: { cases: (Heure | null)[]; hauteur: number; fine?: boolean }) {
  const max = Math.max(1, ...cases.map((c) => c?.n ?? 0));
  const largeur = 240;
  const pas = largeur / cases.length;
  return (
    <svg viewBox={`0 0 ${largeur} ${hauteur}`} preserveAspectRatio="none" className="block w-full" style={{ height: hauteur }} aria-hidden>
      {cases.map((c, i) => {
        const x = i * pas + (fine ? 1 : 2);
        const l = pas - (fine ? 2 : 4);
        if (!c) return <rect key={i} x={x} y={hauteur - 2} width={l} height={2} className="fill-slate-100 dark:fill-slate-700" />;
        const h = Math.max(1.5, (c.n / max) * (hauteur - 2));
        const he = c.e > 0 ? Math.max(1.5, (c.e / max) * (hauteur - 2)) : 0;
        return (
          <g key={i}>
            <title>{c.n}</title>
            <rect x={x} y={hauteur - h} width={l} height={h} rx={1} className="fill-slate-300 dark:fill-slate-600" />
            {he > 0 && <rect x={x} y={hauteur - he} width={l} height={he} rx={1} className="fill-red-500" />}
          </g>
        );
      })}
    </svg>
  );
}

function Metrique({ icone: Icone, libelle, valeur, aide, ton }: {
  icone: LucideIcon;
  libelle: string;
  valeur: string;
  aide: string;
  ton?: 'rouge' | 'ambre' | 'terne';
}) {
  const couleur =
    ton === 'rouge' ? 'text-red-700 dark:text-red-300'
      : ton === 'ambre' ? 'text-amber-700 dark:text-amber-300'
        : ton === 'terne' ? 'font-semibold text-slate-400 dark:text-slate-500'
          : 'text-slate-800 dark:text-slate-100';
  return (
    <div className="bg-white px-3.5 py-2.5 dark:bg-slate-800">
      <div className="flex items-center gap-1.5 text-meta text-slate-500 dark:text-slate-400">
        <Icone size={14} aria-hidden />
        {libelle}
      </div>
      <div className={`mt-0.5 text-lead font-bold tabular-nums ${couleur}`}>{valeur}</div>
      <div className="text-meta text-slate-400 dark:text-slate-500">{aide}</div>
    </div>
  );
}

function Bandeau({ ton, icone: Icone, titre, texte, action }: {
  ton: 'rouge' | 'ambre';
  icone: LucideIcon;
  titre: string;
  texte: string;
  action?: { libelle: string; onClick: () => void };
}) {
  const liseré = ton === 'rouge' ? 'border-l-red-500' : 'border-l-amber-500';
  const pastille = ton === 'rouge'
    ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300';
  return (
    <div role="status" className={`flex flex-wrap items-start gap-3 rounded-xl border border-l-3 border-slate-200 bg-white px-3.5 py-3 shadow-sm dark:border-slate-700 dark:bg-slate-800 ${liseré}`}>
      <div className={`grid h-7.5 w-7.5 shrink-0 place-items-center rounded-full p-1.5 ${pastille}`}>
        <Icone size={16} aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-body font-semibold text-slate-800 dark:text-slate-100">{titre}</p>
        <p className="text-field text-slate-500 dark:text-slate-400">{texte}</p>
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="self-center whitespace-nowrap text-field font-semibold text-slate-800 underline underline-offset-4 touch-manipulation dark:text-slate-100"
        >
          {action.libelle}
        </button>
      )}
    </div>
  );
}

// ── Bandeau d etat : le plus grave d abord ──────────────────────────────────────────────

function BandeauDEtat({ sante, f, ouvrir, allerALaCouverture }: {
  sante: SanteDuParc;
  f: Formats;
  ouvrir: (id: string) => void;
  allerALaCouverture: () => void;
}) {
  const { t } = f;
  const enPanne = sante.apps.find((a) => a.statut === 'ne-repond-pas');
  if (enPanne) {
    const nom = nomDeLApp(enPanne.id, t).nom;
    const cause = enPanne.repond.httpStatus
      ? t('admin:parc.sonde.http', { s: enPanne.repond.httpStatus })
      : enPanne.repond.erreur === 'delai' ? t('admin:parc.sonde.delai') : t('admin:parc.sonde.reseau');
    return (
      <Bandeau ton="rouge" icone={AlertTriangle} titre={t('admin:parc.bandeau.enPanneT', { app: nom })}
        texte={t('admin:parc.bandeau.enPanneS', { cause })}
        action={{ libelle: t('admin:parc.bandeau.voir', { app: nom }), onClick: () => ouvrir(enPanne.id) }} />
    );
  }
  const enErreurs = sante.apps.find((a) => a.statut === 'erreurs');
  if (enErreurs && enErreurs.telemetrie.etat === 'ok') {
    const nom = nomDeLApp(enErreurs.id, t).nom;
    const pct = Math.round((enErreurs.telemetrie.erreurs5xx / Math.max(1, enErreurs.telemetrie.requetes)) * 100);
    return (
      <Bandeau ton="rouge" icone={AlertTriangle} titre={t('admin:parc.bandeau.erreursT', { app: nom, pct })}
        texte={t('admin:parc.bandeau.erreursS')}
        action={{ libelle: t('admin:parc.bandeau.voir', { app: nom }), onClick: () => ouvrir(enErreurs.id) }} />
    );
  }
  const illisibles = sante.apps.filter((a) => a.telemetrie.etat === 'illisible');
  if (illisibles.length > 0) {
    const premiere = illisibles[0]!;
    const motif = premiere.telemetrie.etat === 'illisible' ? libelleMotif(premiere.telemetrie.motif, t) : '';
    const titre = illisibles.length === 1
      ? t('admin:parc.bandeau.illisibleT', { app: nomDeLApp(premiere.id, t).nom })
      : t('admin:parc.bandeau.illisiblesT', { n: illisibles.length });
    return (
      <Bandeau ton="ambre" icone={HelpCircle} titre={titre} texte={t('admin:parc.bandeau.illisibleS', { motif })}
        action={{ libelle: t('admin:parc.bandeau.voir', { app: nomDeLApp(premiere.id, t).nom }), onClick: () => ouvrir(premiere.id) }} />
    );
  }
  const lente = sante.apps.find((a) => a.statut === 'lente');
  if (lente && lente.telemetrie.etat === 'ok' && lente.telemetrie.p95Ms !== null) {
    const nom = nomDeLApp(lente.id, t).nom;
    return (
      <Bandeau ton="ambre" icone={Gauge} titre={t('admin:parc.bandeau.lenteT', { app: nom, v: f.vitesse(lente.telemetrie.p95Ms) })}
        texte={t('admin:parc.bandeau.lenteS')}
        action={{ libelle: t('admin:parc.bandeau.voir', { app: nom }), onClick: () => ouvrir(lente.id) }} />
    );
  }
  const c = totauxCouverture(sante.apps);
  if (c.aveugle > 0) {
    return (
      <Bandeau ton="ambre" icone={EyeOff} titre={t('admin:parc.bandeau.partielT', { b: c.aveugle, t: c.total })}
        texte={t('admin:parc.bandeau.partielS')}
        action={{ libelle: t('admin:parc.bandeau.partielL'), onClick: allerALaCouverture }} />
    );
  }
  return null;
}

// ── Les quatre indicateurs ──────────────────────────────────────────────────────────────

function Indicateur({ children, aide }: { children: ReactNode; aide: string }) {
  return (
    <div>
      {children}
      <p className="mt-1 px-1 text-meta text-slate-500 dark:text-slate-400">{aide}</p>
    </div>
  );
}

function Indicateurs({ sante, f, compterBruit }: { sante: SanteDuParc; f: Formats; compterBruit: boolean }) {
  const { t } = f;
  const repondent = sante.apps.filter((a) => a.repond.repond).length;
  const lisibles = sante.apps.filter((a) => a.telemetrie.etat === 'ok');
  const illisibles = sante.apps.filter((a) => a.telemetrie.etat === 'illisible').length;
  const erreurs = lisibles.reduce((s, a) => s + (a.telemetrie.etat === 'ok' ? a.telemetrie.erreurs5xx : 0), 0)
    + (compterBruit ? sante.apps.reduce((s, a) => s + bruitAffiche(a), 0) : 0);
  const cov = totauxCouverture(sante.apps);
  const couvertureLisible = sante.apps.some((a) => a.alarmes.etat === 'ok');
  return (
    <section className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4" aria-label={t('admin:parc.kpi.aria')}>
      <Indicateur aide={t('admin:parc.kpi.repondentAide')}>
        <KpiCard label={t('admin:parc.kpi.repondent')} value={`${repondent} / ${sante.apps.length}`} icon={Activity}
          variant={repondent === sante.apps.length ? 'green' : 'red'} />
      </Indicateur>
      <Indicateur aide={illisibles > 0 ? t('admin:parc.kpi.erreursIllisibles', { n: illisibles }) : t('admin:parc.kpi.erreursAide')}>
        <KpiCard label={t('admin:parc.kpi.erreurs')}
          value={lisibles.length === 0 ? '?' : illisibles > 0 ? `${f.nombre(erreurs)} + ?` : f.nombre(erreurs)}
          icon={AlertTriangle} variant={erreurs > 0 ? 'red' : illisibles > 0 ? 'orange' : 'green'} />
      </Indicateur>
      <Indicateur aide={sante.declenchements.etat === 'ok'
        ? t('admin:parc.kpi.alarmesAide', { n: sante.declenchements.j30 })
        : t('admin:parc.kpi.alarmesIllisibles', { motif: libelleMotif(sante.declenchements.motif, t) })}>
        <KpiCard label={t('admin:parc.kpi.alarmes')}
          value={sante.declenchements.etat === 'ok' ? String(sante.declenchements.h24) : '?'} icon={Bell}
          variant={sante.declenchements.etat === 'ok' && sante.declenchements.h24 > 0 ? 'red' : 'blue'} />
      </Indicateur>
      <Indicateur aide={couvertureLisible ? t('admin:parc.kpi.couvertureAide') : t('admin:parc.kpi.couvertureIllisible')}>
        <KpiCard label={t('admin:parc.kpi.couverture')}
          value={couvertureLisible ? `${cov.voit} / ${cov.total}` : '?'} icon={Eye}
          variant={couvertureLisible && cov.voit === cov.total ? 'green' : 'orange'} />
      </Indicateur>
    </section>
  );
}

// ── Une carte par application ───────────────────────────────────────────────────────────

function CarteApp({ app, sante, f, compterBruit, onOuvrir }: {
  app: AppSante;
  sante: SanteDuParc;
  f: Formats;
  compterBruit: boolean;
  onOuvrir: () => void;
}) {
  const { t } = f;
  const { nom, role, icone: Icone } = nomDeLApp(app.id, t);
  const tel = app.telemetrie;
  const alarmes = alarmesComptees(app);
  const voient = alarmes.filter((a) => a.couverture.etat === 'voit').length;
  const sonne = alarmes.reduce((s, a) => s + declencheesDepuis24h(a, sante.mesureA).length, 0);
  const redemarragesVus = alarmes.some((a) => a.genre === 'redemarrages' && a.couverture.etat === 'voit');
  const bruit = bruitAffiche(app);

  let erreurs: { v: string; ton?: 'rouge' | 'terne' };
  let vitesse: { v: string; ton?: 'ambre' | 'terne' };
  if (tel.etat === 'illisible') {
    erreurs = { v: t('admin:parc.m.inconnu'), ton: 'terne' };
    vitesse = { v: t('admin:parc.m.inconnu'), ton: 'terne' };
  } else if (tel.etat === 'non-transmis') {
    erreurs = { v: t('admin:parc.m.nonMesure'), ton: 'terne' };
    vitesse = { v: t('admin:parc.m.nonMesure'), ton: 'terne' };
  } else {
    const e = tel.erreurs5xx + (compterBruit ? bruit : 0);
    erreurs = { v: f.compte(e), ton: e > 0 ? 'rouge' : undefined };
    vitesse = tel.p95Ms === null
      ? { v: t('admin:parc.m.nonMesure'), ton: 'terne' }
      : { v: f.vitesse(tel.p95Ms), ton: tel.p95Ms > 3000 ? 'ambre' : undefined };
  }

  const pied = [
    tel.etat === 'illisible' ? t('admin:parc.pied.illisible')
      : tel.etat === 'non-transmis' ? t('admin:parc.pied.nonTransmises')
        : t('admin:parc.pied.demandes', { n: f.nombre(tel.requetes) }),
    tel.etat !== 'illisible' && bruit > 0 ? t('admin:parc.pied.bruit', { n: f.nombre(bruit) }) : null,
  ].filter(Boolean).join(' · ');

  const liseré = app.statut === 'erreurs' || app.statut === 'ne-repond-pas'
    ? 'border-l-3 border-l-red-500'
    : app.statut === 'lente' || app.statut === 'illisible' ? 'border-l-3 border-l-amber-500' : '';

  return (
    <button type="button" onClick={onOuvrir}
      aria-label={`${nom} — ${libelleStatut(app.statut, t)}`}
      className={`flex w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white text-left shadow-sm touch-manipulation hover:border-slate-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:border-slate-700 dark:bg-slate-800 ${liseré}`}>
      <div className="flex w-full items-center gap-2.5 px-3.5 pb-2.5 pt-3.5">
        <div className="grid h-8.5 w-8.5 shrink-0 place-items-center rounded-lg bg-slate-50 p-2 text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          <Icone size={18} aria-hidden />
        </div>
        <div className="min-w-0">
          <p className="text-body font-semibold text-slate-800 dark:text-slate-100">{nom}</p>
          <p className="truncate text-meta text-slate-500 dark:text-slate-400">{role}</p>
        </div>
        <span className="ml-auto"><StatusBadge variant={VARIANTE_STATUT[app.statut]}>{libelleStatut(app.statut, t)}</StatusBadge></span>
      </div>
      <div className="w-full px-3.5 pb-2.5">
        {tel.etat === 'ok' ? <Barres cases={casesHoraires(tel.heures, sante.mesureA)} hauteur={26} fine /> : <div className="h-6.5" />}
      </div>
      <div className="grid w-full grid-cols-2 gap-px border-y border-slate-100 bg-slate-100 dark:border-slate-700 dark:bg-slate-700">
        <Metrique icone={AlertTriangle} libelle={t('admin:parc.m.erreurs')} valeur={erreurs.v} aide={t('admin:parc.m.erreursAide')} ton={erreurs.ton} />
        <Metrique icone={Gauge} libelle={t('admin:parc.m.vitesse')} valeur={vitesse.v} aide={t('admin:parc.m.vitesseAide')} ton={vitesse.ton} />
        <Metrique icone={RotateCcw} libelle={t('admin:parc.m.demarrage')}
          valeur={app.repond.demarreeIlYaS === null ? t('admin:parc.m.nonRapporte') : t('admin:parc.m.ilYa', { d: f.duree(app.repond.demarreeIlYaS) })}
          ton={app.repond.demarreeIlYaS === null ? 'terne' : undefined}
          aide={redemarragesVus ? t('admin:parc.m.demarrageAideVu') : t('admin:parc.m.demarrageAide')} />
        <Metrique icone={Bell} libelle={t('admin:parc.m.alarmes')}
          valeur={app.alarmes.etat !== 'ok' ? t('admin:parc.m.inconnu') : sonne === 0 ? t('admin:parc.m.aucune') : t('admin:parc.m.ontSonne', { n: sonne })}
          ton={app.alarmes.etat !== 'ok' ? 'terne' : sonne > 0 ? 'rouge' : undefined}
          aide={app.alarmes.etat === 'ok' ? t('admin:parc.m.alarmesAide', { s: voient, t: alarmes.length }) : libelleMotif(app.alarmes.motif, t)} />
      </div>
      <div className="flex w-full items-center gap-2 px-3.5 py-2 text-meta text-slate-500 dark:text-slate-400">
        <span className="tabular-nums">{pied}</span>
        <span className="ml-auto inline-flex items-center gap-0.5 font-semibold text-slate-800 dark:text-slate-100">
          {t('admin:parc.pied.detail')}
          <ChevronRight size={14} aria-hidden />
        </span>
      </div>
    </button>
  );
}

// ── La matrice « ce que la surveillance voit » ──────────────────────────────────────────

function Tuile({ c, libelle, onClick }: { c: Couverture; libelle: string; onClick: () => void }) {
  if (c.etat === 'sans-objet') {
    return <span className="inline-flex h-7 w-23 items-center justify-center text-meta text-slate-400">{libelle}</span>;
  }
  const style =
    c.etat === 'voit' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      : c.etat === 'rarement' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
        : 'border border-dashed border-slate-300 text-slate-400 dark:border-slate-600';
  const Icone = c.etat === 'voit' ? Check : c.etat === 'rarement' ? Clock : c.etat === 'inconnue' ? HelpCircle : EyeOff;
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex h-7 w-23 items-center justify-center gap-1 rounded-md text-meta font-semibold touch-manipulation focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 ${style}`}>
      <Icone size={14} aria-hidden />
      {libelle}
    </button>
  );
}

function libelleCouverture(c: Couverture, t: TFunction): string {
  switch (c.etat) {
    case 'voit': return t('admin:parc.cov.voit');
    case 'rarement': return t('admin:parc.cov.rarement');
    case 'aveugle': return t('admin:parc.cov.aveugle');
    case 'inconnue': return t('admin:parc.cov.inconnue');
    case 'sans-objet': return t('admin:parc.cov.sansObjet');
  }
}

function Matrice({ sante, f }: { sante: SanteDuParc; f: Formats }) {
  const { t } = f;
  const [explication, setExplication] = useState<string | null>(null);
  const cov = totauxCouverture(sante.apps);
  const reglesIllisibles = sante.apps.find((a) => a.alarmes.etat === 'illisible');
  return (
    <Carte className="p-3.5">
      <div id="couverture" className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-head font-semibold text-slate-800 dark:text-slate-100">{t('admin:parc.cov.titre')}</h2>
        {!reglesIllisibles && (
          <p className="text-meta text-slate-500 dark:text-slate-400">
            {t('admin:parc.cov.compte', { s: cov.voit, r: cov.rarement, b: cov.aveugle, t: cov.total })}
          </p>
        )}
      </div>
      <p className="mt-1.5 text-meta text-slate-400">{t('admin:parc.cov.intro')}</p>
      {reglesIllisibles && reglesIllisibles.alarmes.etat === 'illisible' ? (
        <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2.5 text-field text-slate-700 dark:bg-slate-900 dark:text-slate-200">
          {t('admin:parc.cov.illisible', { motif: libelleMotif(reglesIllisibles.alarmes.motif, t) })}
        </p>
      ) : (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-190 border-separate border-spacing-0">
            <thead>
              <tr>
                <th scope="col" className="rounded-tl-xl bg-slate-50 px-2.5 py-2 text-left text-meta font-semibold text-slate-500 dark:bg-slate-900 dark:text-slate-400">
                  {t('admin:parc.cov.alarme')}
                </th>
                {sante.apps.map((a) => (
                  <th key={a.id} scope="col" className="bg-slate-50 px-2.5 py-2 text-left text-meta font-semibold text-slate-500 last:rounded-tr-xl dark:bg-slate-900 dark:text-slate-400">
                    {nomDeLApp(a.id, t).nom}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GENRES.map((g) => {
                const lg = libelleGenre(g, t);
                return (
                  <tr key={g}>
                    <th scope="row" className="border-b border-slate-100 px-2.5 py-2 text-left font-medium dark:border-slate-700">
                      <span className="block text-body font-semibold text-slate-800 dark:text-slate-100">{lg.nom}</span>
                      <span className="block max-w-80 text-meta font-normal text-slate-500 dark:text-slate-400">{lg.description}</span>
                    </th>
                    {sante.apps.map((a) => {
                      const alarme = a.alarmes.etat === 'ok' ? a.alarmes.liste.find((x) => x.genre === g) : undefined;
                      const c: Couverture = alarme?.couverture ?? { etat: 'inconnue' };
                      const nom = nomDeLApp(a.id, t).nom;
                      return (
                        <td key={a.id} className="border-b border-slate-100 px-2.5 py-2 dark:border-slate-700">
                          <Tuile c={c} libelle={libelleCouverture(c, t)} onClick={() => setExplication(pourquoi(c, g, nom, t))} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap gap-3.5 text-meta text-slate-500 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-3.5 rounded-sm bg-green-100" />{t('admin:parc.cov.voit')}</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-3.5 rounded-sm bg-amber-100" />{t('admin:parc.cov.rarement')}</span>
        <span className="inline-flex items-center gap-1.5"><i className="inline-block h-2.5 w-3.5 rounded-sm border border-dashed border-slate-400" />{t('admin:parc.cov.aveugle')}</span>
      </div>
      {explication && (
        <p role="status" aria-live="polite" className="mt-2.5 rounded-lg bg-slate-50 px-3 py-2.5 text-field text-slate-700 dark:bg-slate-900 dark:text-slate-200">
          {explication}
        </p>
      )}
    </Carte>
  );
}

// ── Le tiroir de detail ─────────────────────────────────────────────────────────────────

function Stat({ libelle, valeur, aide, ton }: { libelle: string; valeur: string; aide: string; ton?: 'rouge' | 'ambre' | 'terne' }) {
  const couleur = ton === 'rouge' ? 'text-red-700 dark:text-red-300' : ton === 'ambre' ? 'text-amber-700 dark:text-amber-300'
    : ton === 'terne' ? 'text-slate-400' : 'text-slate-800 dark:text-slate-100';
  return (
    <div>
      <p className="text-meta text-slate-500 dark:text-slate-400">{libelle}</p>
      <p className={`text-lead font-bold tabular-nums ${couleur}`}>{valeur}</p>
      <p className="text-meta text-slate-400">{aide}</p>
    </div>
  );
}

function Panneau({ icone: Icone, titre, children }: { icone?: LucideIcon; titre?: string; children: ReactNode }) {
  return (
    <Carte className="p-3.5">
      {titre && (
        <h3 className="mb-2.5 flex items-center gap-2 text-body font-semibold text-slate-800 dark:text-slate-100">
          {Icone && <Icone size={16} aria-hidden />}
          {titre}
        </h3>
      )}
      {children}
    </Carte>
  );
}

function PastilleAlarme({ a, mesureA, f }: { a: Alarme; mesureA: string; f: Formats }) {
  const { t } = f;
  const recente = declencheesDepuis24h(a, mesureA)[0];
  if (recente) return <StatusBadge variant="danger">{t('admin:parc.tiroir.aSonne', { t: f.heure(recente) })}</StatusBadge>;
  switch (a.couverture.etat) {
    case 'voit': return <StatusBadge variant="success">{t('admin:parc.tiroir.calme')}</StatusBadge>;
    case 'rarement': return <StatusBadge variant="warning">{t('admin:parc.tiroir.dort')}</StatusBadge>;
    case 'inconnue': return <StatusBadge variant="neutral">{t('admin:parc.cov.inconnue')}</StatusBadge>;
    default: return <StatusBadge variant="neutral">{t('admin:parc.cov.aveugle')}</StatusBadge>;
  }
}

function Tiroir({ app, sante, f, compterBruit, setCompterBruit, onFermer }: {
  app: AppSante;
  sante: SanteDuParc;
  f: Formats;
  compterBruit: boolean;
  setCompterBruit: (v: boolean) => void;
  onFermer: () => void;
}) {
  const { t } = f;
  const { nom, role } = nomDeLApp(app.id, t);
  const tel = app.telemetrie;
  const bruit = bruitAffiche(app);
  const alarmes = alarmesComptees(app);
  const redemarragesVus = alarmes.some((a) => a.genre === 'redemarrages' && a.couverture.etat === 'voit');
  return (
    <GsDrawer open onClose={onFermer} closeLabel={t('admin:parc.tiroir.fermer')}
      title={<span className="flex items-center gap-2">{nom}<StatusBadge variant={VARIANTE_STATUT[app.statut]}>{libelleStatut(app.statut, t)}</StatusBadge></span>}
      ariaLabel={nom} subtitle={`${app.id} · ${role}`}>
      <div className="space-y-4">
        {tel.etat === 'illisible' ? (
          <Bandeau ton="ambre" icone={HelpCircle} titre={t('admin:parc.tiroir.inconnuT')}
            texte={`${t('admin:parc.tiroir.inconnuS')} (${libelleMotif(tel.motif, t)})`} />
        ) : tel.etat === 'non-transmis' ? (
          <Panneau icone={Activity} titre={t('admin:parc.tiroir.trafic')}>
            <p className="text-meta text-slate-400">{t('admin:parc.tiroir.traficAucun')}</p>
          </Panneau>
        ) : (
          <>
            <Panneau icone={Activity} titre={t('admin:parc.tiroir.trafic')}>
              <Barres cases={casesHoraires(tel.heures, sante.mesureA)} hauteur={120} />
              <div className="mt-1 flex justify-between text-meta text-slate-400">
                <span>{t('admin:parc.tiroir.depuis24h')}</span>
                <span>{t('admin:parc.tiroir.maintenant')}</span>
              </div>
            </Panneau>
            <Panneau>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <Stat libelle={t('admin:parc.tiroir.demandes')} valeur={f.compte(tel.requetes)} aide={t('admin:parc.tiroir.demandesAide')} />
                <Stat libelle={t('admin:parc.tiroir.e5xx')} valeur={f.compte(tel.erreurs5xx + (compterBruit ? bruit : 0))}
                  aide={t('admin:parc.tiroir.e5xxAide')} ton={tel.erreurs5xx > 0 ? 'rouge' : undefined} />
                <Stat libelle={t('admin:parc.tiroir.e4xx')} valeur={f.compte(tel.refus4xx)} aide={t('admin:parc.tiroir.e4xxAide')} />
                <Stat libelle={t('admin:parc.tiroir.p95')} valeur={tel.p95Ms === null ? t('admin:parc.m.nonMesure') : f.vitesse(tel.p95Ms)}
                  aide={t('admin:parc.tiroir.p95Aide')} ton={tel.p95Ms !== null && tel.p95Ms > 3000 ? 'ambre' : undefined} />
                <Stat libelle={t('admin:parc.tiroir.lentes')} valeur={f.compte(tel.lentes)} aide={t('admin:parc.tiroir.lentesAide')}
                  ton={tel.lentes > 0 ? 'ambre' : undefined} />
                <Stat libelle={t('admin:parc.tiroir.exceptions')} valeur={f.compte(tel.exceptions)} aide={t('admin:parc.tiroir.exceptionsAide')}
                  ton={tel.exceptions > 0 ? 'rouge' : undefined} />
              </div>
            </Panneau>
          </>
        )}

        <Panneau icone={RotateCcw} titre={t('admin:parc.tiroir.demarrages')}>
          <p className="text-field text-slate-700 dark:text-slate-200">
            {t('admin:parc.tiroir.dernierDemarrage', {
              v: app.repond.demarreeIlYaS === null ? t('admin:parc.m.nonRapporte') : t('admin:parc.m.ilYa', { d: f.duree(app.repond.demarreeIlYaS) }),
            })}
            {app.repond.version ? ` ${t('admin:parc.tiroir.version', { v: app.repond.version })}` : ''}
            {` ${t('admin:parc.tiroir.sonde', { ms: f.nombre(app.repond.dureeMs) })}`}
          </p>
          <p className="mt-1.5 text-meta text-slate-400">
            {redemarragesVus ? t('admin:parc.tiroir.redemarragesVus') : t('admin:parc.tiroir.redemarragesNonMesures')}
          </p>
        </Panneau>

        <Panneau icone={Bell} titre={t('admin:parc.tiroir.alarmes')}>
          {app.alarmes.etat !== 'ok' ? (
            <p className="text-field text-slate-700 dark:text-slate-200">{t('admin:parc.cov.illisible', { motif: libelleMotif(app.alarmes.motif, t) })}</p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-700">
              {alarmes.map((a) => {
                const lg = libelleGenre(a.genre, t);
                return (
                  <li key={a.genre} className="flex items-start gap-2.5 py-2 first:pt-0">
                    <div className="min-w-0">
                      <p className="text-field font-semibold text-slate-800 dark:text-slate-100">{lg.nom}</p>
                      <p className="text-meta text-slate-500 dark:text-slate-400">{lg.description}</p>
                      {a.declenchements.length > 0 && (
                        <p className="text-meta text-slate-400">{t('admin:parc.tiroir.sur30j', { n: a.declenchements.length, t: f.jourHeure(a.declenchements[0]!) })}</p>
                      )}
                    </div>
                    <span className="ml-auto shrink-0"><PastilleAlarme a={a} mesureA={sante.mesureA} f={f} /></span>
                  </li>
                );
              })}
            </ul>
          )}
        </Panneau>

        {tel.etat !== 'illisible' && (
          <Panneau icone={Filter} titre={t('admin:parc.tiroir.bruit')}>
            {bruit > 0 ? (
              <>
                <p className="text-field font-bold tabular-nums text-slate-800 dark:text-slate-100">{t('admin:parc.tiroir.bruitN', { n: f.nombre(bruit) })}</p>
                <p className="mt-1.5 text-meta text-slate-400">{t('admin:parc.tiroir.bruitPourquoi')}</p>
                <label className="mt-2.5 flex items-center gap-2.5 text-field text-slate-700 dark:text-slate-200">
                  <GsToggle checked={compterBruit} onCheckedChange={setCompterBruit} aria-label={t('admin:parc.tiroir.bruitCompter')} />
                  {t('admin:parc.tiroir.bruitCompter')}
                </label>
              </>
            ) : (
              <p className="text-meta text-slate-400">{t('admin:parc.tiroir.bruitAucun')}</p>
            )}
          </Panneau>
        )}

        {app.ressource && (
          <a href={`https://portal.azure.com/#resource${app.ressource}/overview`} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-field font-semibold text-slate-700 touch-manipulation hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700">
            <ExternalLink size={16} aria-hidden />
            {t('admin:parc.tiroir.portail')}
          </a>
        )}
        <p className="text-meta text-slate-400">{t('admin:parc.tiroir.source', { a: app.id, t: f.heure(sante.mesureA) })}</p>
      </div>
    </GsDrawer>
  );
}

// ── La page ─────────────────────────────────────────────────────────────────────────────

export default function SanteDuParcPage() {
  const f = useFormats();
  const { t } = f;
  const requete = useParcSante();
  const remesurer = useRemesurerParcSante();
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [compterBruit, setCompterBruit] = useState(false);

  if (requete.isPending) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-2 text-field text-slate-500">
        <RefreshCw size={16} className="animate-spin" aria-hidden />
        {t('admin:parc.chargement')}
      </div>
    );
  }
  if (requete.error instanceof ApiForbiddenError) {
    // Un administrateur DELEGUE voit l entree de menu (le client ne connait pas la double-cle)
    // et recoit le refus du serveur : on le dit, sans le deguiser en panne.
    return (
      <div className="mx-auto max-w-lg py-12 text-center">
        <Shield size={40} className="mx-auto mb-4 text-slate-300 dark:text-slate-600" aria-hidden />
        <p className="text-body font-semibold text-slate-800 dark:text-slate-100">{t('admin:parc.refuseT')}</p>
        <p className="mt-1 text-field text-slate-500 dark:text-slate-400">{t('admin:parc.refuseS')}</p>
      </div>
    );
  }
  if (requete.isError || !requete.data) {
    return <QueryError message={t('admin:parc.erreur')} onRetry={() => void requete.refetch()} />;
  }

  const sante = requete.data;
  const appOuverte = sante.apps.find((a) => a.id === ouverte);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('admin:parc.titre')}
        description={t('admin:parc.sousTitre', { n: sante.apps.length })}
        actions={
          <>
            <span className="inline-flex items-center gap-1.5 text-meta text-slate-500 dark:text-slate-400">
              <Clock size={14} aria-hidden />
              {t('admin:parc.mesure', { t: f.heure(sante.mesureA) })}
            </span>
            <button type="button" onClick={() => remesurer.mutate()} disabled={remesurer.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-field font-semibold text-slate-800 touch-manipulation hover:bg-slate-200 disabled:cursor-progress disabled:opacity-60 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600">
              <RefreshCw size={16} className={remesurer.isPending ? 'animate-spin' : ''} aria-hidden />
              {remesurer.isPending ? t('admin:parc.remesureEnCours') : t('admin:parc.remesurer')}
            </button>
          </>
        }
      />

      <BandeauDEtat sante={sante} f={f} ouvrir={setOuverte}
        allerALaCouverture={() => document.getElementById('couverture')?.scrollIntoView({ behavior: 'smooth' })} />

      <Indicateurs sante={sante} f={f} compterBruit={compterBruit} />

      <section aria-labelledby="titre-apps">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 id="titre-apps" className="text-head font-semibold text-slate-800 dark:text-slate-100">{t('admin:parc.apps.titre')}</h2>
          <p className="text-meta text-slate-500 dark:text-slate-400">{t('admin:parc.apps.aide')}</p>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {sante.apps.map((app) => (
            <CarteApp key={app.id} app={app} sante={sante} f={f} compterBruit={compterBruit} onOuvrir={() => setOuverte(app.id)} />
          ))}
        </div>
      </section>

      <Matrice sante={sante} f={f} />

      {appOuverte && (
        <Tiroir app={appOuverte} sante={sante} f={f} compterBruit={compterBruit} setCompterBruit={setCompterBruit}
          onFermer={() => setOuverte(null)} />
      )}
    </div>
  );
}
